export interface RealtimeCallbacks {
  onStatus(message: string): void;
  onParticipantText(text: string, messageId?: string): void;
  onAssistantText(text: string, messageId?: string): void;
  onDelegation(input: string, delegationId: string): Promise<void>;
  onInterrupted(): void;
  onRemoteAudioAttached(): void;
  onClosed(): void;
  onPlaybackComplete?(text: string, delegationId: string): void;
}

export interface RealtimeProfile {
  name: string;
  wakePhrase: string;
}

export interface RealtimeBridge {
  readonly connected: boolean;
  readonly lastSpokenText: string;
  start(appSessionId: string, disclosure: string, profile: RealtimeProfile): Promise<void>;
  stop(): Promise<void>;
  interrupt(): void;
  /** Legacy method name: plays validated text literally, never model commentary. */
  sendCommentary(delegationId: string, spokenText: string): void;
}

class LocalVoiceUnavailable extends Error {
  constructor() { super("No local speech voice is available. Read the answer in the document."); }
}

function localSpeechVoice(signal?: AbortSignal): Promise<SpeechSynthesisVoice> {
  const synthesis = window.speechSynthesis;
  const choose = () => {
    const local = synthesis.getVoices().filter(voice => voice.localService === true);
    return local.find(voice => voice.lang.toLowerCase() === 'en-us') ??
      local.find(voice => /^en(?:-|$)/i.test(voice.lang)) ?? local[0];
  };
  if (signal?.aborted) return Promise.reject(new DOMException("Voice playback was cancelled.", "AbortError"));
  const ready = choose();
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve, reject) => {
    const finish = (voice?: SpeechSynthesisVoice, error?: Error) => {
      window.clearTimeout(timer);
      synthesis.removeEventListener('voiceschanged', changed);
      signal?.removeEventListener('abort', cancel);
      if (voice) resolve(voice); else reject(error ?? new LocalVoiceUnavailable());
    };
    const changed = () => { const voice = choose(); if (voice) finish(voice); };
    const cancel = () => finish(undefined, new DOMException("Voice playback was cancelled.", "AbortError"));
    const timer = window.setTimeout(() => finish(), 1_500);
    synthesis.addEventListener('voiceschanged', changed);
    signal?.addEventListener('abort', cancel, {once:true});
    changed();
  });
}

function speakText(text: string, signal?: AbortSignal, disclosure = false): Promise<void> {
  if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) {
    return Promise.reject(new Error(disclosure ? "This browser cannot speak the required disclosure." : "This browser cannot play the spoken answer."));
  }
  const play = (voice?: SpeechSynthesisVoice) => new Promise<void>((resolve, reject) => {
    const cancelled = () => new DOMException(disclosure ? "Voice startup was cancelled." : "Voice playback was cancelled.", "AbortError");
    if (signal?.aborted) { reject(cancelled()); return; }
    const utterance = new SpeechSynthesisUtterance(text);
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", cancel);
      utterance.onend = null;
      utterance.onerror = null;
      if (error) reject(error); else resolve();
    };
    const cancel = () => { finish(cancelled()); window.speechSynthesis.cancel(); };
    if (voice) utterance.voice = voice;
    utterance.lang = voice?.lang ?? "en-US";
    utterance.rate = 0.96;
    utterance.onend = () => finish();
    const failed = () => new Error(disclosure ? "The required disclosure could not be played." : "The spoken answer could not be played.");
    utterance.onerror = () => finish(failed());
    signal?.addEventListener("abort", cancel, { once: true });
    window.speechSynthesis.cancel();
    try { window.speechSynthesis.speak(utterance); } catch { finish(failed()); }
  });
  return disclosure ? play() : localSpeechVoice(signal).then(play);
}

/** Literal, application-owned audio. Completion means utterance end, never enqueue
 * or a Live context acknowledgement. Epochs fence cancelled queued work. */
class VerifiedSpeechQueue {
  private tail = Promise.resolve();
  private epoch = 0;
  private active?: AbortController;
  private accepted = new Set<string>();
  private completedText = "";

  constructor(private readonly callbacks: RealtimeCallbacks) {}

  get lastSpokenText() { return this.completedText; }

  reset() {
    this.cancel();
    this.accepted.clear();
    this.completedText = "";
  }

  enqueue(delegationId: string, text: string) {
    if (!text.trim() || this.accepted.has(delegationId)) return;
    this.accepted.add(delegationId);
    const epoch = this.epoch;
    this.tail = this.tail.then(async () => {
      if (epoch !== this.epoch) return;
      const abort = new AbortController();
      this.active = abort;
      this.callbacks.onStatus("Speaking the answer…");
      try {
        await speakText(text, abort.signal);
        if (epoch !== this.epoch) return;
        this.completedText = text;
        this.callbacks.onPlaybackComplete?.(text, delegationId);
        this.callbacks.onStatus("Listening.");
      } catch (error) {
        if (epoch === this.epoch && !abort.signal.aborted)
          this.callbacks.onStatus(error instanceof LocalVoiceUnavailable ? error.message : "The spoken answer could not be played. You can read it in the document.");
      } finally {
        if (this.active === abort) this.active = undefined;
      }
    });
  }

  cancel() {
    ++this.epoch;
    this.active?.abort();
    this.active = undefined;
    this.tail = Promise.resolve();
  }
}

async function microphoneStream() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Microphone access is not available in this browser.");
  }
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

async function waitForIce(connection: RTCPeerConnection) {
  if (connection.iceGatheringState === "complete") return;
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      connection.removeEventListener("icegatheringstatechange", onState);
      reject(new Error("Timed out while preparing the voice connection."));
    }, 10_000);
    function onState() {
      if (connection.iceGatheringState !== "complete") return;
      window.clearTimeout(timeout);
      connection.removeEventListener("icegatheringstatechange", onState);
      resolve();
    }
    connection.addEventListener("icegatheringstatechange", onState);
    onState();
  });
}

// Live context appends allow 500 tokens. A 400-byte UTF-8 ceiling is conservative
// without a tokenizer, and code-point iteration keeps Unicode characters intact.
function spokenChunks(text: string) {
  const encoder = new TextEncoder();
  const chunks: string[] = [];
  let chunk = "", bytes = 0;
  for (const character of text) {
    const size = encoder.encode(character).length;
    if (bytes + size > 400) { chunks.push(chunk); chunk = ""; bytes = 0; }
    chunk += character; bytes += size;
  }
  if (chunk) chunks.push(chunk);
  return chunks;
}

export class JurisRealtimeBridge implements RealtimeBridge {
  private peer?: RTCPeerConnection;
  private events?: RTCDataChannel;
  private microphone?: MediaStream;
  private audio = new Audio();
  private liveSessionId?: string;
  private speech: VerifiedSpeechQueue;
  private stopping = false;
  private closedNotified = false;
  private lifecycle = 0;
  private startupAbort?: AbortController;
  private closePromise?: Promise<void>;
  private closeResolve?: () => void;
  private closeTimer?: number;
  private seenEvents = new Set<string>();
  private seenDelegations = new Set<string>();
  private replyDelegations = new Set<string>();
  private pendingDelegation?: { id: string; offset: number; emptyDeadline: number; quietAfter: number; timer?: number };
  private inputPauseTimer?: number;
  private inputOverflow = false;
  private inputFragments: Array<{ text: string; start: number; end: number }> = [];
  private inputGroups: Array<{ id: string; after: number; until: number; fragments: Array<{ text: string; start: number; end: number }> }> = [];
  private inputBoundary = -1;
  private ignoredThrough = -1;
  private latestOffset = -1;
  private waitingForFreshInput = false;
  private profile: RealtimeProfile = { name: "the assistant", wakePhrase: "the configured wake phrase" };

  constructor(
    private readonly callbacks: RealtimeCallbacks,
    private readonly createSession: (input: {
      appSessionId: string;
      disclosureAccepted: true;
      sdp: string;
    }) => Promise<any>,
  ) {
    this.speech = new VerifiedSpeechQueue(callbacks);
    this.audio.autoplay = false;
    this.audio.muted = true;
    this.audio.volume = 0;
  }

  get connected() {
    return this.events?.readyState === "open";
  }

  get lastSpokenText() {
    return this.speech.lastSpokenText;
  }

  private send(event: object) {
    if (!this.events || this.events.readyState !== "open") {
      throw new Error("The Realtime event channel is not ready.");
    }
    this.events.send(JSON.stringify(event));
  }

  async start(appSessionId: string, disclosure: string, profile: RealtimeProfile) {
    const lifecycle = ++this.lifecycle;
    this.startupAbort = new AbortController();
    this.stopping = false;
    this.closedNotified = false;
    this.closePromise = undefined;
    this.audio.muted = true;
    this.speech.reset();
    this.seenEvents.clear(); this.seenDelegations.clear(); this.replyDelegations.clear();
    this.inputBoundary = -1; this.ignoredThrough = -1; this.latestOffset = -1; this.waitingForFreshInput = false;
    this.profile = profile;
    try {
    this.callbacks.onStatus("Speaking the required disclosure before microphone access…");
    await speakText(disclosure, this.startupAbort.signal, true);
    if (lifecycle !== this.lifecycle || this.stopping) throw new Error("Voice startup was cancelled.");
    this.callbacks.onStatus("Requesting microphone access…");
    const microphone = await microphoneStream();
    if (lifecycle !== this.lifecycle || this.stopping) { microphone.getTracks().forEach((track) => track.stop()); throw new Error("Voice startup was cancelled."); }
    this.microphone = microphone;

    const peer = new RTCPeerConnection();
    this.peer = peer;
    peer.addEventListener("track", (event) => {
      if (this.stopping || this.peer !== peer) return;
      this.audio.muted = true;
      this.audio.volume = 0;
      this.audio.srcObject = new MediaStream([event.track]);
      this.callbacks.onRemoteAudioAttached();
    });
    this.microphone.getAudioTracks().forEach((track) => peer.addTrack(track, this.microphone!));
    const events = peer.createDataChannel("oai-events");
    this.events = events;
    events.addEventListener("message", (message) => { if (this.events === events) this.handleEvent(message); });
    events.addEventListener("close", () => {
      if (this.events !== events) return;
      this.finishClose("The voice connection closed before finalization was confirmed.");
    });

    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await waitForIce(peer);
    if (lifecycle !== this.lifecycle || this.stopping) throw new Error("Voice startup was cancelled.");
    const sdp = peer.localDescription?.sdp;
    if (!sdp) throw new Error("The browser did not produce a voice-session offer.");

    const result = await this.createSession({ appSessionId, disclosureAccepted: true, sdp });
    if (lifecycle !== this.lifecycle || this.stopping) throw new Error("Voice startup was cancelled.");
    this.liveSessionId = result.session.id;
    await peer.setRemoteDescription({ type: "answer", sdp: result.transport.sdp });
    } catch (error) { await this.stop(); throw error; }
  }

  private caption(event: Record<string, any>) {
    if (typeof event.delta !== "string" || !event.delta || !Number.isFinite(event.start_ms) || !Number.isFinite(event.end_ms)) return;
    const fragment = { text: event.delta, start: event.start_ms as number, end: event.end_ms as number };
    if (fragment.start < 0 || fragment.end < fragment.start) return;
    if (this.waitingForFreshInput && fragment.end <= this.ignoredThrough) return;
    this.latestOffset = Math.max(this.latestOffset, fragment.end);
    const joined = (fragments: typeof this.inputFragments) => fragments.map((item) => item.text).join("").trim();
    this.waitingForFreshInput = false;
    let group = this.inputGroups.find((item) => fragment.start > item.after && fragment.start <= item.until);
    if (!group) {
      group = { id: `voice-user-${crypto.randomUUID()}`, after: this.inputBoundary, until: Infinity, fragments: [] };
      this.inputGroups.push(group);
      if (this.inputGroups.length > 32) this.inputGroups.shift();
    }
    if (!group.fragments.length) {
      // Barge-in invalidates speech from an earlier delegated task, while its
      // already-running document operation may still finish and reconcile.
      this.replyDelegations.clear();
      this.speech.cancel();
      this.callbacks.onInterrupted();
    }
    group.fragments.push(fragment);
    if (group.fragments.length > 256) group.fragments.shift();
    this.callbacks.onParticipantText(joined(group.fragments), group.id);
    if (fragment.end > this.ignoredThrough) {
      if (this.inputOverflow || this.inputFragments.length >= 256) {
        // Never submit the tail of an utterance after silently losing its prefix.
        this.cancelDelegations(); this.inputOverflow = true; this.inputFragments = [];
        this.ignoredThrough = Math.max(this.ignoredThrough, fragment.end);
        window.clearTimeout(this.inputPauseTimer);
        this.inputPauseTimer = window.setTimeout(() => {
          this.inputOverflow = false;
          this.inputBoundary = this.ignoredThrough;
          const open = this.inputGroups.find((entry) => entry.until === Infinity);
          if (open) open.until = this.inputBoundary;
        }, 1_000);
        this.callbacks.onStatus("Please pause, then ask a shorter question. The unfinished voice request was not submitted.");
        return;
      }
      this.inputFragments.push(fragment);
    }
    if (this.pendingDelegation) {
      // A delegation offset is metadata, not an utterance-end boundary.
      this.pendingDelegation.quietAfter = Date.now() + 1_000;
      this.scheduleDelegation();
    }
  }

  private scheduleDelegation() {
    const pending = this.pendingDelegation;
    if (!pending || this.stopping) return;
    window.clearTimeout(pending.timer);
    pending.timer = window.setTimeout(() => {
      if (this.stopping || this.pendingDelegation !== pending) return;
      const fragments = [...this.inputFragments];
      const input = fragments.map((item) => item.text).join("").trim();
      if (!input && Date.now() < pending.emptyDeadline) { this.scheduleDelegation(); return; }
      this.pendingDelegation = undefined;
      if (!input) { this.callbacks.onStatus("The voice request had no transcript. Please repeat the question."); return; }
      this.inputFragments = [];
      this.inputBoundary = Math.max(this.inputBoundary, pending.offset, ...fragments.map((fragment) => fragment.end));
      const openGroup = this.inputGroups.find((group) => group.until === Infinity);
      if (openGroup) openGroup.until = this.inputBoundary;
      this.replyDelegations.add(pending.id);
      if (this.replyDelegations.size > 256) this.replyDelegations.delete(this.replyDelegations.values().next().value!);
      void Promise.resolve().then(() => { if (!this.stopping && this.replyDelegations.has(pending.id)) return this.callbacks.onDelegation(input, pending.id); }).catch(() => { if (!this.stopping) this.callbacks.onStatus("The voice request could not be completed. Please try again."); });
    }, Math.max(0, (this.inputFragments.length ? pending.quietAfter : pending.emptyDeadline) - Date.now()));
  }

  private handleEvent(message: MessageEvent<string>) {
    let event: Record<string, any>;
    try { event = JSON.parse(message.data); } catch { return; }
    if (!event || typeof event.type !== "string" || (this.stopping && event.type !== "session.closed")) return;
    if (typeof event.event_id === "string") {
      if (this.seenEvents.has(event.event_id)) return;
      this.seenEvents.add(event.event_id);
      if (this.seenEvents.size > 2048) this.seenEvents.delete(this.seenEvents.values().next().value!);
    }
    switch (event.type) {
      case "session.started":
        this.liveSessionId = event.session.id;
        this.callbacks.onStatus(`Connected. Say ‘${this.profile.wakePhrase}’ or ask a direct question.`);
        break;
      case "session.input_transcript.delta":
        this.caption(event);
        break;
      case "session.output_transcript.delta":
        // Native generated text/audio is not an authorized answer channel.
        break;
      case "session.delegation.created": {
        const delegationId = event.delegation?.id;
        if (event.delegation?.target !== "client" || typeof delegationId !== "string" || !delegationId || this.seenDelegations.has(delegationId)) break;
        const offset = Number.isFinite(event.offset_ms) ? event.offset_ms : this.latestOffset;
        if (offset <= this.ignoredThrough) break;
        this.seenDelegations.add(delegationId);
        if (this.seenDelegations.size > 256) this.seenDelegations.delete(this.seenDelegations.values().next().value!);
        if (this.inputOverflow) break;
        if (!this.inputGroups.some((group) => group.until === Infinity)) this.inputGroups.push({ id: `voice-user-${crypto.randomUUID()}`, after: this.inputBoundary, until: Infinity, fragments: [] });
        if (this.pendingDelegation) {
          // Keep one pending task as the user supplies more detail. The latest
          // delegation receives the complete verified answer after input is quiet.
          this.pendingDelegation.id = delegationId;
          this.pendingDelegation.offset = Math.max(this.pendingDelegation.offset, offset);
          this.pendingDelegation.quietAfter = Date.now() + 1_000;
        } else this.pendingDelegation = { id: delegationId, offset, emptyDeadline: Date.now() + 3_000, quietAfter: Date.now() + 1_000 };
        this.scheduleDelegation();
        break;
      }
      case "session.closed":
        this.finishClose("Voice session ended cleanly.");
        break;
      case "error":
        this.callbacks.onStatus("The Realtime service reported an error. End the session and retry.");
        break;
      default:
        break;
    }
  }

  sendCommentary(delegationId: string, spokenText: string) {
    if (this.stopping || !this.connected || !this.replyDelegations.has(delegationId)) return;
    this.replyDelegations.delete(delegationId);
    this.waitingForFreshInput = false;
    this.speech.enqueue(delegationId, spokenText);
    try {
      for (const content of spokenChunks(spokenText)) this.send({
        type: "session.thinking.append", event_id: crypto.randomUUID(), delegation_id: delegationId, content,
      });
    } catch {
      this.callbacks.onStatus("The voice context could not be updated. The answer remains in your document.");
    }
  }

  interrupt() {
    this.speech.cancel();
    if (!this.connected || this.stopping) return;
    this.cancelDelegations();
    this.replyDelegations.clear();
    this.ignoredThrough = Math.max(this.ignoredThrough, this.latestOffset);
    this.inputBoundary = this.ignoredThrough;
    this.inputFragments = [];
    this.inputGroups = [];
    this.waitingForFreshInput = true;
    this.audio.muted = true;
    this.send({ type: "session.instructions.append", event_id: crypto.randomUUID(), delegation_id: null,
      content: "Stop speaking now. The user changed the question or architecture scope. Wait for the application's verified result before continuing." });
  }

  async stop() {
    if (this.closePromise) return this.closePromise;
    this.stopping = true;
    this.lifecycle += 1;
    this.startupAbort?.abort();
    this.speech.cancel();
    this.cancelDelegations();
    this.microphone?.getTracks().forEach((track) => track.stop());
    this.microphone = undefined;
    this.audio.muted = true;
    if (!this.connected) { this.finishClose("The voice connection closed."); return; }
    this.closePromise = new Promise<void>((resolve) => { this.closeResolve = resolve; });
    this.closeTimer = window.setTimeout(() => this.finishClose("Voice disconnected; session finalization was not confirmed."), 5_000);
    this.send({ type: "session.close", event_id: crypto.randomUUID() });
    return this.closePromise;
  }

  private cancelDelegations() {
    window.clearTimeout(this.pendingDelegation?.timer);
    this.pendingDelegation = undefined;
    window.clearTimeout(this.inputPauseTimer);
    this.inputOverflow = false;
  }

  private finishClose(status: string) {
    this.stopping = true;
    this.speech.cancel();
    this.cancelDelegations();
    window.clearTimeout(this.closeTimer);
    this.microphone?.getTracks().forEach((track) => track.stop());
    const events = this.events, peer = this.peer;
    this.microphone = undefined;
    this.events = undefined;
    this.peer = undefined;
    this.liveSessionId = undefined;
    events?.close();
    peer?.close();
    this.audio.pause();
    this.audio.srcObject = null;
    this.inputFragments = [];
    this.inputGroups = [];
    this.replyDelegations.clear();
    this.closeResolve?.();
    this.closeResolve = undefined;
    if (!this.closedNotified) { this.closedNotified = true; this.callbacks.onStatus(status); this.callbacks.onClosed(); }
  }
}

export class FakeRealtimeBridge implements RealtimeBridge {
  private active = false;
  private microphone?: MediaStream;
  private speech: VerifiedSpeechQueue;
  private lifecycle = 0;
  private startupAbort?: AbortController;
  private seenDelegations = new Set<string>();
  private replyDelegations = new Set<string>();

  constructor(private readonly callbacks: RealtimeCallbacks) {
    this.speech = new VerifiedSpeechQueue(callbacks);
  }

  get connected() {
    return this.active;
  }

  get lastSpokenText() {
    return this.speech.lastSpokenText;
  }

  async start(appSessionId: string, disclosure: string, profile: RealtimeProfile) {
    const lifecycle = ++this.lifecycle;
    this.startupAbort = new AbortController();
    this.speech.reset();
    this.seenDelegations.clear();
    this.replyDelegations.clear();
    try {
      this.callbacks.onStatus("Speaking the required disclosure before microphone access…");
      await speakText(disclosure, this.startupAbort.signal, true);
      if (lifecycle !== this.lifecycle) throw new Error("Voice startup was cancelled.");
      this.callbacks.onStatus("Requesting microphone access…");
      const microphone = await microphoneStream();
      if (lifecycle !== this.lifecycle) { microphone.getTracks().forEach(track => track.stop()); throw new Error("Voice startup was cancelled."); }
      this.microphone = microphone;
      const response = await fetch("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appSessionId, disclosureAccepted: true, sdp: "fake-offer" }),
      });
      if (lifecycle !== this.lifecycle) throw new Error("Voice startup was cancelled.");
      if (!response.ok) throw new Error((await response.json()).error ?? "Fake Realtime session failed.");
      this.active = true;
      this.callbacks.onStatus(`Connected. Say ‘${profile.wakePhrase}’ or ask a direct question.`);
    } catch (error) {
      if (lifecycle === this.lifecycle) this.cleanup();
      throw error;
    }
  }

  async simulateVoice(text: string, delegationId: string = crypto.randomUUID()) {
    if (!this.active) throw new Error("Start the voice session first.");
    if (this.seenDelegations.has(delegationId)) return;
    this.seenDelegations.add(delegationId);
    this.replyDelegations.clear();
    this.replyDelegations.add(delegationId);
    this.speech.cancel();
    this.callbacks.onInterrupted();
    this.callbacks.onParticipantText(text);
    await this.callbacks.onDelegation(text, delegationId);
  }

  simulateDisconnect() {
    this.cleanup();
    this.callbacks.onStatus("The voice connection closed unexpectedly. Your document is still readable.");
    this.callbacks.onClosed();
  }

  sendCommentary(delegationId: string, spokenText: string) {
    if (!this.active || !this.replyDelegations.has(delegationId)) return;
    this.replyDelegations.delete(delegationId);
    this.speech.enqueue(delegationId, spokenText);
  }

  interrupt() {
    this.speech.cancel();
    this.replyDelegations.clear();
    this.callbacks.onInterrupted();
  }

  async stop() {
    this.cleanup();
    this.callbacks.onStatus("Voice session ended cleanly.");
    this.callbacks.onClosed();
  }

  private cleanup() {
    ++this.lifecycle;
    this.startupAbort?.abort();
    this.speech.cancel();
    this.replyDelegations.clear();
    this.active = false;
    this.microphone?.getTracks().forEach((track) => track.stop());
    this.microphone = undefined;
  }
}
